/**
 * The multi-chart workspace: a grid of resizable, moveable chart tiles.
 *
 * Why not a grid-layout library (react-grid-layout, dockview, muuri):
 *   - Each adds 50-200KB of JS for behaviour this view needs a specific
 *     flavour of (pop-out windows, cross-tab sync).
 *   - They all assume a single-window workspace, so a dependency would
 *     have to be worked around for the thing I actually want.
 *   - The whole file is one component and ~250 lines; a library would be
 *     larger than the thing it replaced.
 *
 * The grid is twelve columns wide. Each cell is one unit tall; the grid
 * grows downward as more tiles are added. Positions are stored in cells,
 * not pixels, so a layout that is "two by two" stays two by two at any
 * window size and on every device.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { ChartTile } from "./ChartTile";
import { useTileCatalogue } from "./useTileRuns";
import { CELL_MIN, GRID_COLS, type Tile, useWorkspace } from "./workspace";

/** The pixel height assigned to one grid row.
 *
 *  A row is deliberately short -- about a quarter the natural chart height.
 *  A two-by-two layout then wants four rows per tile, which is enough that
 *  resizing feels fluid and few enough that snapping between sizes is
 *  obvious. If this were pegged to a chart's "natural" height, resizing
 *  below that would clip the chart; this way a tile can be made genuinely
 *  small.
 */
const ROW_PX = 96;

/** Which window instance this is: either the main workspace or a pop-out.
 *
 *  A pop-out carries a tile id in its URL hash and renders only that tile,
 *  full-size. Pop-outs still share the workspace through the hook, so a
 *  change in one appears in the other.
 */
export function MultiChart(): JSX.Element {
  const popoutId = usePopoutHash();
  if (popoutId) return <PopoutView tileId={popoutId} />;
  return <GridView />;
}

function GridView(): JSX.Element {
  const { ws, addTile, removeTile, updateTile, resetLayout, setCrosshairSync } = useWorkspace();
  const catalogue = useTileCatalogue();
  const [drag, setDrag] = useState<DragState | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  // Rows: enough to hold every tile, plus one empty row to drop into so a
  // tile can always be moved below the current layout.
  const maxRow = ws.tiles.reduce((m, t) => Math.max(m, t.y + t.h), 0);
  const rows = Math.max(8, maxRow + 1);

  const onDragStart = useCallback((tile: Tile, ev: React.PointerEvent) => {
    const grid = gridRef.current;
    if (!grid) return;
    ev.preventDefault();
    (ev.target as Element).setPointerCapture?.(ev.pointerId);
    const r = grid.getBoundingClientRect();
    const cellW = r.width / GRID_COLS;
    const cellH = ROW_PX;
    setDrag({
      id: tile.id, kind: "move",
      startX: ev.clientX, startY: ev.clientY,
      origX: tile.x, origY: tile.y, origW: tile.w, origH: tile.h,
      cellW, cellH,
      currentX: tile.x, currentY: tile.y, currentW: tile.w, currentH: tile.h,
    });
  }, []);

  const onResizeStart = useCallback((tile: Tile, ev: React.PointerEvent) => {
    const grid = gridRef.current;
    if (!grid) return;
    ev.preventDefault();
    ev.stopPropagation();
    (ev.target as Element).setPointerCapture?.(ev.pointerId);
    const r = grid.getBoundingClientRect();
    const cellW = r.width / GRID_COLS;
    const cellH = ROW_PX;
    setDrag({
      id: tile.id, kind: "resize",
      startX: ev.clientX, startY: ev.clientY,
      origX: tile.x, origY: tile.y, origW: tile.w, origH: tile.h,
      cellW, cellH,
      currentX: tile.x, currentY: tile.y, currentW: tile.w, currentH: tile.h,
    });
  }, []);

  // One listener on the window during a drag, not per-tile. A tile cannot
  // reliably see pointermove outside its own bounds without this.
  useEffect(() => {
    if (!drag) return;
    const move = (ev: PointerEvent) => {
      const dx = (ev.clientX - drag.startX) / drag.cellW;
      const dy = (ev.clientY - drag.startY) / drag.cellH;
      let x = drag.origX, y = drag.origY, w = drag.origW, h = drag.origH;
      if (drag.kind === "move") {
        x = clamp(Math.round(drag.origX + dx), 0, GRID_COLS - drag.origW);
        y = Math.max(0, Math.round(drag.origY + dy));
      } else {
        w = clamp(Math.round(drag.origW + dx), CELL_MIN, GRID_COLS - drag.origX);
        h = Math.max(CELL_MIN, Math.round(drag.origH + dy));
      }
      setDrag((d) => d && { ...d, currentX: x, currentY: y, currentW: w, currentH: h });
    };
    const end = () => {
      setDrag((d) => {
        if (!d) return null;
        // Commit the drag at the end rather than on every move, so a drag
        // produces one layout write instead of thirty, and so a layout
        // conflict (another window edited at the same time) is less likely.
        if (d.kind === "move") {
          const spot = resolve(ws.tiles, d.id, d.currentX, d.currentY, d.origW, d.origH);
          updateTile(d.id, { x: spot.x, y: spot.y });
        } else {
          updateTile(d.id, { w: d.currentW, h: d.currentH });
        }
        return null;
      });
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
    };
  }, [drag, ws.tiles, updateTile]);

  const popOut = useCallback((tileId: string) => {
    // About 1200x800 is enough to feel like a real window on a laptop; the
    // user can resize it afterwards. `noopener` would break the broadcast
    // channel contract on some browsers, so it is omitted deliberately.
    window.open(
      `${location.pathname}?view=workspace#tile=${encodeURIComponent(tileId)}`,
      `tt-tile-${tileId}`,
      "width=1200,height=800",
    );
  }, []);

  return (
    <div className="mc-root">
      <div className="mc-bar">
        <span className="mc-bar-title">Workspace</span>
        <span className="mc-bar-sub">
          {ws.tiles.length} chart{ws.tiles.length === 1 ? "" : "s"} ·
          drag the header to move · drag the corner to resize
        </span>
        <span className="mc-flex" />
        <label className="mc-check" title="Hovering one chart highlights the same time on every other chart.">
          <input
            type="checkbox"
            checked={ws.crosshairSync}
            onChange={(e) => setCrosshairSync(e.target.checked)}
          />
          Sync crosshair
        </label>
        <button className="mc-btn" onClick={() => addTile("AAPL", "5m")}>
          + Add chart
        </button>
        <button className="mc-btn mc-btn-ghost" onClick={resetLayout} title="Discard the current layout">
          Reset
        </button>
      </div>

      <div
        className="mc-grid"
        ref={gridRef}
        style={{
          gridTemplateColumns: `repeat(${GRID_COLS}, 1fr)`,
          gridAutoRows: `${ROW_PX}px`,
          gridTemplateRows: `repeat(${rows}, ${ROW_PX}px)`,
        }}
      >
        {ws.tiles.map((tile) => {
          const d = drag?.id === tile.id ? drag : null;
          const x = d?.currentX ?? tile.x;
          const y = d?.currentY ?? tile.y;
          const w = d?.currentW ?? tile.w;
          const h = d?.currentH ?? tile.h;
          return (
            <div
              key={tile.id}
              className="mc-cell"
              style={{
                gridColumn: `${x + 1} / span ${w}`,
                gridRow: `${y + 1} / span ${h}`,
                zIndex: d ? 50 : 1,
              }}
            >
              <ChartTile
                tile={tile}
                catalogue={catalogue}
                crosshairSync={ws.crosshairSync}
                onChange={(patch) => updateTile(tile.id, patch)}
                onRemove={() => removeTile(tile.id)}
                onPopOut={() => popOut(tile.id)}
                onDragStart={(ev) => onDragStart(tile, ev)}
                onResizeStart={(ev) => onResizeStart(tile, ev)}
                dragging={!!d}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** A single-tile window for pop-outs. Reads the same workspace storage, so
 *  changes made here propagate back to the main window immediately. */
function PopoutView({ tileId }: { tileId: string }): JSX.Element {
  const { ws, updateTile, removeTile } = useWorkspace();
  const catalogue = useTileCatalogue();
  const tile = ws.tiles.find((t) => t.id === tileId);
  if (!tile) {
    return (
      <div className="mc-msg mc-dim" style={{ padding: 24 }}>
        This chart has been closed in the main window.
      </div>
    );
  }
  return (
    <div className="mc-popout">
      <ChartTile
        tile={tile}
        catalogue={catalogue}
        crosshairSync={ws.crosshairSync}
        onChange={(patch) => updateTile(tile.id, patch)}
        onRemove={() => { removeTile(tile.id); window.close(); }}
        onPopOut={() => { /* already popped out */ }}
        onDragStart={() => { /* no drag in a single-tile window */ }}
        onResizeStart={() => { /* no resize in a single-tile window */ }}
        dragging={false}
      />
    </div>
  );
}

function usePopoutHash(): string | null {
  const [id, setId] = useState<string | null>(() => parsePopoutHash());
  useEffect(() => {
    const update = () => setId(parsePopoutHash());
    window.addEventListener("hashchange", update);
    return () => window.removeEventListener("hashchange", update);
  }, []);
  return id;
}

function parsePopoutHash(): string | null {
  const h = window.location.hash;
  if (!h.startsWith("#tile=")) return null;
  return decodeURIComponent(h.slice(6)) || null;
}

interface DragState {
  id: string;
  kind: "move" | "resize";
  startX: number; startY: number;
  origX: number; origY: number; origW: number; origH: number;
  currentX: number; currentY: number; currentW: number; currentH: number;
  cellW: number; cellH: number;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/** Resolve a dropped position so tiles do not overlap.
 *
 *  If the dropped-at position is clear, keep it. Otherwise walk outward in
 *  rings until a free spot is found. "Overlap and let the user sort it out"
 *  looks like a bug; silently moving the dropped tile to the nearest free
 *  cell is what every other grid tool does and what the user expects.
 */
function resolve(
  tiles: Tile[], movingId: string,
  x: number, y: number, w: number, h: number,
): { x: number; y: number } {
  const others = tiles.filter((t) => t.id !== movingId);
  if (!overlaps(others, x, y, w, h)) return { x, y };
  for (let r = 1; r < 20; r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.abs(dx) !== r && Math.abs(dy) !== r) continue;
        const nx = clamp(x + dx, 0, GRID_COLS - w);
        const ny = Math.max(0, y + dy);
        if (!overlaps(others, nx, ny, w, h)) return { x: nx, y: ny };
      }
    }
  }
  // Fallback: tack it on below everything.
  const bottom = others.reduce((m, t) => Math.max(m, t.y + t.h), 0);
  return { x: 0, y: bottom };
}

function overlaps(tiles: Tile[], x: number, y: number, w: number, h: number): boolean {
  for (const t of tiles) {
    if (x + w <= t.x || t.x + t.w <= x) continue;
    if (y + h <= t.y || t.y + t.h <= y) continue;
    return true;
  }
  return false;
}
